/**
 * Calculate linear regression for trend line
 * @param data Array of numbers (y values)
 * @returns Array of regression line values matching input length
 */
export function calculateLinearRegression(data: (number | null)[]): number[] {
    // Filter out null values and keep track of indices
    const validPoints: { index: number; value: number }[] = [];
    data.forEach((val, idx) => {
        if (val !== null && val !== undefined) {
            validPoints.push({ index: idx, value: val });
        }
    });

    if (validPoints.length < 2) {
        // Not enough data for regression
        return data.map(v => v || 0);
    }

    // Calculate linear regression using least squares method
    const n = validPoints.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

    validPoints.forEach(point => {
        sumX += point.index;
        sumY += point.value;
        sumXY += point.index * point.value;
        sumX2 += point.index * point.index;
    });

    // Linear regression formula: y = a + bx
    const b = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const a = (sumY - b * sumX) / n;

    // Generate regression line for all points
    return data.map((_, idx) => a + b * idx);
}

/**
 * Calculate exponential moving average for smoother trend
 * @param data Array of numbers
 * @param period Number of periods for EMA calculation
 * @returns Array of EMA values
 */
export function calculateEMA(data: (number | null)[], period: number = 3): number[] {
    const cleanData = data.map(v => v || 0);

    if (cleanData.length === 0) return [];

    const ema: number[] = [];
    const multiplier = 2 / (period + 1);

    // First EMA is simple average of first period values
    let sum = 0;
    for (let i = 0; i < Math.min(period, cleanData.length); i++) {
        sum += cleanData[i];
    }
    ema[0] = sum / Math.min(period, cleanData.length);

    // Calculate EMA for remaining values
    for (let i = 1; i < cleanData.length; i++) {
        ema[i] = cleanData[i] * multiplier + ema[i - 1] * (1 - multiplier);
    }

    return ema;
}

/**
 * Determine trend direction (up/down)
 * @param data Array of numbers
 * @returns 'up', 'down', or 'stable'
 */
export function calculateTrendDirection(data: (number | null)[]): 'up' | 'down' | 'stable' {
    const validData = data.filter(v => v !== null && v !== undefined) as number[];

    if (validData.length < 2) return 'stable';

    const firstHalf = validData.slice(0, Math.floor(validData.length / 2));
    const secondHalf = validData.slice(Math.floor(validData.length / 2));

    const avgFirst = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
    const avgSecond = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;

    const change = avgSecond - avgFirst;
    const threshold = 0.1; // 0.1 stars

    if (change > threshold) return 'up';
    if (change < -threshold) return 'down';
    return 'stable';
}

/**
 * Calculate trend percentage change using linear regression
 * This reflects the actual trend by analyzing the slope across all data points
 * @param data Array of numbers
 * @returns Percentage change as a number (e.g., 5 for +5%, -10 for -10%)
 */
export function calculateTrendPercentage(data: (number | null)[]): number {
    // Filter out null values and keep track of indices
    const validPoints: { index: number; value: number }[] = [];
    data.forEach((val, idx) => {
        if (val !== null && val !== undefined) {
            validPoints.push({ index: idx, value: val });
        }
    });

    if (validPoints.length < 2) return 0;

    // Calculate linear regression coefficients
    const n = validPoints.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

    validPoints.forEach(point => {
        sumX += point.index;
        sumY += point.value;
        sumXY += point.index * point.value;
        sumX2 += point.index * point.index;
    });

    // Linear regression formula: y = a + bx
    const b = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const a = (sumY - b * sumX) / n;

    // Get the first and last valid indices
    const firstIndex = validPoints[0].index;
    const lastIndex = validPoints[validPoints.length - 1].index;

    // Calculate estimated values at start and end using the regression line
    const estimatedStart = a + b * firstIndex;
    const estimatedEnd = a + b * lastIndex;

    // Avoid division by zero
    if (estimatedStart === 0) return 0;

    // Calculate percentage change based on trend line
    return ((estimatedEnd - estimatedStart) / Math.abs(estimatedStart)) * 100;
}
